import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  Alert,
  Modal,
  Badge,
} from 'react-bootstrap';
import OperationProgress from '../components/OperationProgress';
import { clearBatchProgress, initBatchProgress, tickBatchProgress } from '../utils/batchProgress';

function ImportFailures() {
  const { importLogId } = useParams();
  const navigate = useNavigate();
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingFailure, setEditingFailure] = useState(null);
  const [correctedData, setCorrectedData] = useState({});
  const [retrying, setRetrying] = useState(false);
  const [retryProgress, setRetryProgress] = useState(null);
  const [savingCorrection, setSavingCorrection] = useState(false);
  const [saveProgress, setSaveProgress] = useState(null);
  const [fixAllSimilar, setFixAllSimilar] = useState(false);
  const [similarFailuresCount, setSimilarFailuresCount] = useState(0);

  useEffect(() => {
    fetchFailures();
  }, [importLogId]);

  const fetchFailures = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/csv/failures/${importLogId}`);
      setFailures(response.data);
    } catch (error) {
      setError('Failed to fetch import failures');
      console.error('Error fetching failures:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditFailure = (failure) => {
    // Parse the original_data if it's a JSON string
    const originalData =
      typeof failure.original_data === 'string'
        ? JSON.parse(failure.original_data)
        : failure.original_data;

    // Parse the corrected_data if it exists and is a JSON string
    const correctedData = failure.corrected_data
      ? typeof failure.corrected_data === 'string'
        ? JSON.parse(failure.corrected_data)
        : failure.corrected_data
      : originalData;

    setEditingFailure({
      ...failure,
      original_data: originalData,
    });
    setCorrectedData(correctedData);
    setFixAllSimilar(false);

    // Count similar failures (same error_message and error_field, and not resolved)
    const similarFailures = failures.filter(
      (f) =>
        f.error_message === failure.error_message &&
        f.error_field === failure.error_field &&
        f.status !== 'resolved' &&
        f.id !== failure.id
    );
    setSimilarFailuresCount(similarFailures.length + 1); // +1 to include current failure
  };

  const handleSaveCorrection = async () => {
    try {
      setSavingCorrection(true);
      if (fixAllSimilar) {
        // Get all similar failures
        const similarFailures = failures.filter(
          (f) =>
            f.error_message === editingFailure.error_message &&
            f.error_field === editingFailure.error_field &&
            f.status !== 'resolved'
        );

        initBatchProgress(setSaveProgress, {
          title: 'Saving corrections...',
          total: similarFailures.length,
        });

        // Determine which field(s) changed by comparing with original data
        const changedFields = {};
        const originalData = editingFailure.original_data;
        Object.keys(correctedData).forEach((key) => {
          if (correctedData[key] !== originalData[key]) {
            changedFields[key] = correctedData[key];
          }
        });

        // If we have an error_field, prioritize updating only that field
        // This ensures we only fix the validation error, not overwrite all data
        const fieldsToUpdate = editingFailure.error_field
          ? { [editingFailure.error_field]: correctedData[editingFailure.error_field] }
          : changedFields;

        // Update all similar failures with only the corrected field(s)
        let successCount = 0;
        let failCount = 0;

        for (const failure of similarFailures) {
          try {
            // Parse the failure's original data
            const failureOriginalData =
              typeof failure.original_data === 'string'
                ? JSON.parse(failure.original_data)
                : failure.original_data;

            // Merge only the corrected field(s) with the failure's original data
            const failureCorrectedData = {
              ...failureOriginalData,
              ...fieldsToUpdate,
            };

            await axios.put(`/api/csv/failures/${failure.id}`, {
              corrected_data: failureCorrectedData,
            });
            successCount++;
            tickBatchProgress(setSaveProgress, { success: true });
          } catch (err) {
            console.error(`Failed to update failure ${failure.id}:`, err);
            failCount++;
            tickBatchProgress(setSaveProgress, { failed: true });
          }
        }

        if (failCount > 0) {
          setSuccess(`${successCount} correction(s) saved successfully. ${failCount} failed.`);
        } else {
          setSuccess(`${successCount} correction(s) saved successfully`);
        }
      } else {
        initBatchProgress(setSaveProgress, { title: 'Saving correction...', total: 1 });
        await axios.put(`/api/csv/failures/${editingFailure.id}`, {
          corrected_data: correctedData,
        });
        tickBatchProgress(setSaveProgress, { success: true });
        setSuccess('Correction saved successfully');
      }

      setEditingFailure(null);
      fetchFailures(); // Refresh the list
    } catch (error) {
      setError('Failed to save correction');
      console.error('Error saving correction:', error);
    } finally {
      setSavingCorrection(false);
      clearBatchProgress(setSaveProgress);
    }
  };

  const pollRetryStatus = async (logId) => {
    while (true) {
      const response = await axios.get(`/api/csv/import-status/${logId}`);
      const data = response.data;
      setRetryProgress({
        processed: data.records_processed || 0,
        total: data.estimated_total_rows || 0,
        success: data.records_successful || 0,
        failed: data.records_failed || 0,
        percent: data.progress_percent || 0,
        status: data.status,
      });

      if (data.status === 'completed' || data.status === 'failed') {
        return data;
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  };

  const handleRetryFailures = async () => {
    try {
      setRetrying(true);
      setRetryProgress({
        processed: 0,
        total: 0,
        success: 0,
        failed: 0,
        percent: 0,
        status: 'retrying',
      });

      const response = await axios.post(`/api/csv/retry-failures/${importLogId}`);
      const logId = response.data.log_id || importLogId;
      const finalStatus = await pollRetryStatus(logId);

      if (finalStatus.status === 'failed') {
        setError(finalStatus.error_details || 'Retry failed');
      } else {
        setSuccess(
          `Retry completed: ${finalStatus.records_successful || 0} successful, ${finalStatus.records_failed || 0} failed`
        );
      }
      fetchFailures();
    } catch (error) {
      setError(error.response?.data?.error || error.message || 'Failed to retry failures');
      console.error('Error retrying failures:', error);
    } finally {
      setRetrying(false);
      setRetryProgress(null);
    }
  };

  const canRetryFailure = (failure) =>
    failure.corrected_data && (failure.status === 'corrected' || failure.status === 'retry_failed');

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      corrected: 'info',
      retry_failed: 'danger',
      retried: 'success',
      resolved: 'success',
      failed: 'danger',
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const renderFieldInput = (key, value) => {
    return (
      <Form.Group key={key} className="mb-2">
        <Form.Label className="small fw-bold">{key}</Form.Label>
        <Form.Control
          type="text"
          value={value || ''}
          onChange={(e) =>
            setCorrectedData({
              ...correctedData,
              [key]: e.target.value,
            })
          }
          size="sm"
        />
      </Form.Group>
    );
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1>Import Failures</h1>
              <p className="text-muted">Review and correct failed import records</p>
            </div>
            <div>
              <Button
                variant="outline-secondary"
                onClick={() => navigate('/import-export')}
                className="me-2"
              >
                Back to Import/Export
              </Button>
              {failures.some((f) => f.status === 'corrected') && (
                <Button variant="success" onClick={handleRetryFailures} disabled={retrying}>
                  {retrying ? 'Retrying...' : 'Retry Corrected Records'}
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {retryProgress && (
        <OperationProgress
          title="Retrying corrected records..."
          processed={retryProgress.processed}
          total={retryProgress.total}
          success={retryProgress.success}
          failed={retryProgress.failed}
          variant="success"
        />
      )}

      <Card>
        <Card.Header>
          <h5 className="mb-0">Failed Records ({failures.length})</h5>
        </Card.Header>
        <Card.Body>
          {failures.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">No import failures found for this import.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Status</th>
                    <th>Error</th>
                    <th>Field</th>
                    <th>Retry Count</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {failures.map((failure) => (
                    <tr key={failure.id}>
                      <td>{failure.row_number}</td>
                      <td>{getStatusBadge(failure.status)}</td>
                      <td>
                        <div
                          className="text-truncate"
                          style={{ maxWidth: '300px' }}
                          title={failure.error_message}
                        >
                          {failure.error_message}
                        </div>
                      </td>
                      <td>
                        {failure.error_field && (
                          <Badge bg="light" text="dark">
                            {failure.error_field}
                          </Badge>
                        )}
                      </td>
                      <td>{failure.retry_count ?? 0}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditFailure(failure)}
                          disabled={failure.status === 'resolved'}
                        >
                          {failure.status === 'resolved' ? 'Resolved' : 'Edit'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Edit Failure Modal */}
      <Modal
        show={!!editingFailure}
        onHide={() => setEditingFailure(null)}
        size="lg"
        style={{ zIndex: 1060 }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Correct Import Failure - Row {editingFailure?.row_number}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {saveProgress && (
            <OperationProgress
              title={saveProgress.title}
              processed={saveProgress.processed}
              total={saveProgress.total}
              success={saveProgress.success}
              failed={saveProgress.failed}
              variant="primary"
              className="mb-3"
            />
          )}
          {editingFailure && (
            <>
              <Alert variant="danger" className="mb-3">
                <strong>Error:</strong> {editingFailure.error_message}
                {editingFailure.error_field && (
                  <div className="mt-1">
                    <strong>Field:</strong> {editingFailure.error_field}
                  </div>
                )}
              </Alert>

              {similarFailuresCount > 1 && (
                <Alert variant="warning" className="mb-3">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <div>
                      <strong>{similarFailuresCount} similar failures found!</strong>
                      <div className="small mt-1">
                        Multiple rows have the same error. You can fix all of them at once.
                      </div>
                    </div>
                  </div>
                </Alert>
              )}

              <Row>
                <Col md={6}>
                  <h6>Original Data</h6>
                  <div
                    className="bg-light p-3 rounded mb-3"
                    style={{ maxHeight: '300px', overflow: 'auto' }}
                  >
                    {Object.entries(editingFailure.original_data).map(([key, value]) => (
                      <div key={key} className="mb-1">
                        <strong>{key}:</strong> {value || <em className="text-muted">empty</em>}
                      </div>
                    ))}
                  </div>
                </Col>
                <Col md={6}>
                  <h6>Corrected Data</h6>
                  <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                    {Object.entries(correctedData).map(([key, value]) =>
                      renderFieldInput(key, value)
                    )}
                  </div>
                </Col>
              </Row>

              {similarFailuresCount > 1 && (
                <div className="mt-3">
                  <Form.Check
                    type="checkbox"
                    id="fix-all-similar"
                    label={`Apply this correction to all ${similarFailuresCount} similar failures`}
                    checked={fixAllSimilar}
                    onChange={(e) => setFixAllSimilar(e.target.checked)}
                  />
                  <Form.Text className="text-muted">
                    {editingFailure.error_field
                      ? `This will update only the "${editingFailure.error_field}" field on all ${similarFailuresCount} rows with the same error. Other fields will remain unchanged.`
                      : 'This will update all rows with the same error message and field.'}
                  </Form.Text>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setEditingFailure(null)}
            disabled={savingCorrection}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveCorrection} disabled={savingCorrection}>
            {savingCorrection
              ? 'Saving...'
              : fixAllSimilar && similarFailuresCount > 1
                ? `Save All ${similarFailuresCount} Corrections`
                : 'Save Correction'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ImportFailures;
